import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["checked", "indeterminate", "onChange", "onClick", "size", "tabIndex", "className", "style", "breakpoints"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/web.dom-collections.for-each.js";
import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { pxToEm, pxToRem, alpha, contrast, getDimmedTextColor, createStyleFunction } from 'utils/ui';
import { fillParent } from 'styles/mixins';
import useMergedRefs from 'hooks/useMergedRefs';
import Icon from 'components/Icon';
import { useTabIndexContext } from 'components/TabIndexContext';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var SIZE_MAP = {
  small: 18,
  default: 21
};
// eslint-disable-next-line @typescript-eslint/no-empty-function
var noop = function noop() {};
var Wrapper = styled.span.withConfig({
  displayName: "Checkbox__Wrapper",
  componentId: "sc-rxonnb-0"
})(["position:relative;display:inline-flex;"]);
var EXTEND_TAP_AREA_SPACING = pxToRem(6);
var Input = styled.input.withConfig({
  displayName: "Checkbox__Input",
  componentId: "sc-rxonnb-1"
})(["position:absolute;top:-", ";left:-", ";width:calc(100% + ", " * 2);height:calc(100% + ", " * 2);opacity:0;", ""], EXTEND_TAP_AREA_SPACING, EXTEND_TAP_AREA_SPACING, EXTEND_TAP_AREA_SPACING, EXTEND_TAP_AREA_SPACING, function (p) {
  return p.disabled && 'cursor: not-allowed;';
});
var sizeStyleProp = createStyleFunction([{
  styleProp: 'size',
  properties: ['fontSize'],
  getValue: function getValue(size) {
    return pxToRem(SIZE_MAP[size]);
  }
}]);
var MockBox = styled.span.withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !['isChecked', 'size'].includes(prop);
  }
}).withConfig({
  displayName: "Checkbox__MockBox",
  componentId: "sc-rxonnb-2"
})(["position:relative;pointer-events:none;display:inline-flex;align-items:center;justify-content:center;width:", ";height:", ";", ";color:", ";background:", ";border:1px solid ", ";border-radius:", ";input[disabled] + &{opacity:0.5;", ";}&::before,&::after{content:'';", " opacity:0;transition:opacity 0.25s linear;pointer-events:none;}&::before{background:", ";border-radius:inherit;", ":hover &{opacity:", ";transition-duration:50ms;}}&::after{top:-3px;left:-3px;bottom:-3px;right:-3px;border-radius:inherit;box-shadow:0 0 0 3px ", ";input.focus-visible + &{opacity:1;transition-duration:50ms;}}& > svg{position:relative;transition:opacity 150ms ease-out;opacity:", ";}"], pxToEm(16), pxToEm(16), sizeStyleProp, function (p) {
  return contrast(p.theme.links);
}, function (p) {
  return p.isChecked ? p.theme.links : p.theme.background;
}, function (p) {
  return alpha(p.theme.text, p.isChecked ? 0 : 0.5);
}, pxToRem(3), function (p) {
  if (!p.isChecked) {
    var textColor = getDimmedTextColor(p.theme);
    return css(["background-image:linear-gradient( 45deg,white 25%,", " 25%,", " 50%,white 50%,white 75%,", " 75%,", " 100% );background-size:4px 4px;"], textColor, textColor, textColor, textColor);
  }
}, fillParent, function (p) {
  return p.theme.shade;
}, Wrapper, function (p) {
  return Math.min(p.theme.shadeStrength, 0.12);
}, function (p) {
  return p.theme.links;
}, function (p) {
  return p.isChecked ? 1 : 0;
});
var Checkbox = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var checked = _ref.checked,
    indeterminate = _ref.indeterminate,
    onChange = _ref.onChange,
    onClick = _ref.onClick,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'small' : _ref$size,
    tabIndex = _ref.tabIndex,
    className = _ref.className,
    style = _ref.style,
    breakpoints = _ref.breakpoints,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var inputRef = useRef(null);
  var mergedRefs = useMergedRefs([ref, inputRef]);
  var tabIndexContext = useTabIndexContext();
  useEffect(function () {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate || false;
    }
  }, [indeterminate]);
  var handleClick = useCallback(function (e) {
    if (onClick) onClick(e);

    /**
     * IE 11 and Edge don't fire onChange for indeterminate checkboxes,
     * therefore we're (ab)using the click handler to do so instead.
     *
     * Surprisingly, this even works with keyboard navigation
     * (i.e. when pressing Space when the input is focused).
     */
    if (indeterminate && onChange) {
      onChange(e);
    }
  }, [onClick, onChange, indeterminate]);
  var isChecked = Boolean(checked);
  return /*#__PURE__*/_jsxs(Wrapper, {
    className: className,
    style: style,
    children: [/*#__PURE__*/_jsx(Input, _objectSpread({
      type: "checkbox",
      ref: mergedRefs,
      checked: isChecked,
      onChange: indeterminate ? noop : onChange,
      onClick: handleClick,
      tabIndex: tabIndex || tabIndexContext
    }, otherProps)), /*#__PURE__*/_jsx(MockBox, {
      "aria-hidden": "true",
      isChecked: isChecked || indeterminate || false,
      size: size,
      breakpoints: breakpoints,
      children: /*#__PURE__*/_jsx(Icon, {
        name: indeterminate ? 'minus' : 'ok',
        scale: 0.8889
      })
    })]
  });
});
Checkbox.displayName = 'Checkbox';
export default Checkbox;